import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../routes/routeKeys";

import DownArrow from "../../assets/svgs/down-arrow.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";

import ActionsMenu from "../../components/ActionsMenu";
import ColumnCustomizer from "../../components/ColumnCustomizer";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import KpiSlider from "../../components/KpiSlider";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialUnderContractFilterData } from "../../utils/initialData";
import ExecutedFilter from "../Executed LOI/component/ExecutedFilter";
import LeadDataContainer from "../Leads/components/LeadDataContainer";
import AddUnderContractModal from "./AddUnderContractModal";
import ConvertClosedModal from "./components/ConvertClosedModal";
import FellOutContract from "./components/FellOutContract";
import EditUnderContractData from "./EditUnderContractModal";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const UnderContract = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);

  // ==================== States ====================
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [undercontractDashboard, setUnderContractDashboard] = useState(null);

  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedRows, setExpandedRows] = useState({});

  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  });

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showfelloutcontract, setShowFellOutContract] = useState(false);

  // Filters
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialUnderContractFilterData;
  });

  // ==================== Columns ====================
  const allColumns = [
    { key: 'details', label: 'Details', fixed: true },
    { key: 'contract_date', label: 'Contract Date', fixed: true },
    { key: 'property_name', label: 'Property Name' },
    { key: 'acquisition', label: 'Acquisition' },
    { key: 'disposition', label: 'Disposition' },
    { key: 'lease_type', label: 'Lease Type' },
    { key: 'lease_term_remaining', label: 'Lease Term Remaining' },
    { key: 'noi', label: 'NOI' },
    { key: 'contract_price', label: 'Contract Price' },
    { key: 'contract_cap_rate', label: 'Contract Cap Rate' },
    { key: 'asking_price_percent', label: '% of Asking Price' },
    { key: 'gci_to_company', label: 'GCI to Company' },
    { key: 'actions', label: 'Actions', fixed: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('underContractVisibleColumns');
    return saved ? JSON.parse(saved) : allColumns.map(col => col.key);
  });

  const [filterStatus, setFilterStatus] = useState({
    new_under_contract_quarter: false,
    total_under_contract_transaction: false,
    under_contract_buyer_transaction: false,
    under_contract_seller_transaction: false,
    under_contrac_gcl_to_company_ytd: false,
    avg_commission_per_transaction_ytd: false,
    buyer_initial_offer_to_under_contract_conversion_rate: false,
    exclusive_under_contract_conversion: false,
    jan: false, feb: false, mar: false, apr: false, may: false,
    jun: false, jul: false, aug: false, sep: false, oct: false,
    nov: false, dec: false,
    gci_closing_quarter1: false,
    gci_closing_quarter2: false,
    gci_closing_quarter3: false,
    gci_closing_quarter4: false,
  });

  // Search
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState('');

  // View Toggle - Only My & Team (Matching Executed LOI)
  const viewOptions = [
    { value: 1, label: "My Under Contract" },
    { value: 3, label: "Team Under Contract" },
  ];
  const [activeView, setActiveView] = useState(3); // Default: Team

  // ==================== Effects ====================
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    search.trim() ? params.set('search', search) : params.delete('search');
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate]);

  useEffect(() => {
    fetchData();
    fetchDashboardData();
  }, [activeView, currentPage, itemPerPage, search, sortBy, sortDirection]);

  // ==================== API Calls ====================
  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}&search=${search}`;

    const {
      created_at, property_name, noi, created_end_date, created_start_date,
      min_contract_price, max_contract_price, min_contract_cap_rate, max_contract_cap_rate,
      min_asking_price, max_asking_price, broker
    } = filters || filterData;

    if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`;
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`;
    if (property_name) params += `&property_name=${encodeURIComponent(property_name)}`;
    if (noi) params += `&noi=${encodeURIComponent(noi)}`;
    if (broker) params += `&broker=${encodeURIComponent(broker)}`;

    if (min_contract_price) params += `&min_contract_price=${encodeURIComponent(min_contract_price)}`;
    if (max_contract_price) params += `&max_contract_price=${encodeURIComponent(max_contract_price)}`;
    if (min_contract_cap_rate) params += `&min_contract_cap_rate=${encodeURIComponent(min_contract_cap_rate)}`;
    if (max_contract_cap_rate) params += `&max_contract_cap_rate=${encodeURIComponent(max_contract_cap_rate)}`;
    if (min_asking_price) params += `&min_asking_price=${encodeURIComponent(min_asking_price)}`;
    if (max_asking_price) params += `&max_asking_price=${encodeURIComponent(max_asking_price)}`;

    Object.entries(filterStatus).forEach(([key, value]) => {
      if (value) params += `&${key}=true`;
    });

    if (sortBy !== "id") {
      params += `&sort_by=${sortBy}&sort_direction=${sortDirection}`;
    }

    return params;
  };

  const fetchData = () => {
    setLoading(true);
    const queryParams = buildQueryParams();

    axios
      .get(`${BASE_URL}/contract-listing?active_tab=${activeView}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.contract || {};
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchDashboardData = () => {
    axios
      .get(`${BASE_URL}/dashboard-under-contract?active_tab=${activeView}`, config)
      .then((res) => {
        setUnderContractDashboard(res?.data?.under_contract_dashboard);
      })
      .catch((err) => console.error(err));
  };

  const onSuccess = () => {
    fetchData();
    fetchDashboardData();
  };

  // ==================== Handlers ====================
  const handleViewChange = (e) => {
    const newView = parseInt(e.target.value);
    setActiveView(newView);
    setCurrentPage(1);
    setExpandedRows({});
  };

  const handleBoxClick = (key) => {
    setActiveView(activeView === 1 ? 1 : 3); // Stay in list view
    setCurrentPage(1);

    const newFilterStatus = { ...filterStatus };
    // Reset all and activate only selected one
    Object.keys(newFilterStatus).forEach(k => newFilterStatus[k] = false);
    newFilterStatus[key] = true;
    setFilterStatus(newFilterStatus);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchInput(newValue.startsWith(" ") ? newValue.trimStart() : newValue);
  };

  const handleDelete = () => {
    setDisableDelete(true);
    axios.delete(`${BASE_URL}/delete-contract/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) NotificationManager.success(res.data.message);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleConfirm = () => {
    axios.post(`${BASE_URL}/mark-contract/${selectedId}`, { mark_contract: true }, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowConfirmModal(false);
        if (res?.data?.message) NotificationManager.success(res.data.message);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  // ==================== JSX ====================
  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">Under Contract</p>

        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center w-full md:w-auto flex-wrap">
            {/* My / Team Toggle */}
            <div className="relative min-w-[240px]">
              <select
                value={activeView}
                onChange={handleViewChange}
                className="w-full bg-white rounded-lg px-4 py-2.5 body-S dark-M cursor-pointer appearance-none"
              >
                {viewOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                <img src={DownArrow} alt="down" className="w-4 h-4" />
              </div>
            </div>

            <ExecutedFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              from="Under Contract"
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts relative flex-1 min-w-[250px]">
              <input
                type="text"
                className="body-S w-full"
                placeholder="Search name, address, city, state"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>

            <button
              className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
              onClick={() => setShowColumnModal(true)}
            >
              Columns ⚙️
            </button>
          </div>
        </div>
      </div>

      {/* ==================== KPI SLIDER (Always Visible) ==================== */}
      {undercontractDashboard && (
        <div className="mb-8 w-full">
          <KpiSlider
            data={[
              { text: "NEW UNDER CONTRACT THIS QUARTER", value: undercontractDashboard.new_under_contract_quarter, key: "new_under_contract_quarter" },
              { text: "TOTAL TRANSACTIONS UNDER CONTRACT YTD", value: undercontractDashboard.total_under_contract_transaction, key: "total_under_contract_transaction" },
              { text: "BUYER TRANSACTIONS UNDER CONTRACT YTD", value: undercontractDashboard.under_contract_buyer_transaction, key: "under_contract_buyer_transaction" },
              { text: "SELLER TRANSACTIONS UNDER CONTRACT YTD", value: undercontractDashboard.under_contract_seller_transaction, key: "under_contract_seller_transaction" },
              { text: "UNDER CONTRACT GCI TO COMPANY", value: `$${undercontractDashboard.under_contrac_gcl_to_company_ytd?.toLocaleString() || 0}`, key: "under_contrac_gcl_to_company_ytd" },
              { text: "AVG. COMMISSION PER TRANSACTION", value: `$${undercontractDashboard.avg_commission_per_transaction_ytd?.toLocaleString() || 0}`, key: "avg_commission_per_transaction_ytd" },
              { text: "BUYER INITIAL OFFER TO UC CONVERSION RATE", value: undercontractDashboard.buyer_initial_offer_to_under_contract_conversion_rate, key: "buyer_initial_offer_to_under_contract_conversion_rate" },
              { text: "EXCLUSIVE TO UC CONVERSION RATE", value: undercontractDashboard.exclusive_under_contract_conversion, key: "exclusive_under_contract_conversion" },
            ]}
            renderItem={(item) => (
              <div onClick={() => handleBoxClick(item.key)} className="cursor-pointer">
                <LeadDataContainer text={item.text} number={item.value} />
              </div>
            )}
          />
        </div>
      )}

      {/* ==================== TABLE ==================== */}
      <div className="light-bg-L pb-3 table-container">
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
          {loading ? (
            <Loader />
          ) : listData?.length === 0 ? (
            <p className="body-N text-center mt-6 p-10">No Under Contract Available</p>
          ) : (
            <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    {visibleColumns.includes('details') && (
                      <th><p>Details</p></th>
                    )}
                    {visibleColumns.includes('contract_date') && (
                      <th className="green-H">
                        <div className="table-header">
                          Contract Date
                          <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('property_name') && (
                      <th>
                        <div className="table-header">
                          PROPERTY NAME
                          <img role="button" src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("property_name"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('acquisition') && (
                      <th>
                        <div className="table-header">
                          Acquisition
                          <img
                            role="button"
                            src={sortBy !== "acquistion" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("acquistion");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('disposition') && (
                      <th>
                        <div className="table-header">
                          Disposition
                          <img
                            role="button"
                            src={sortBy !== "disposition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("disposition");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('lease_type') && (
                      <th>
                        <div className="table-header">
                          Lease Type
                          <img
                            role="button"
                            src={sortBy !== "lease_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("lease_type");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('lease_term_remaining') && (
                      <th>
                        <div className="table-header">
                          Lease Term Remaining
                          <img
                            role="button"
                            src={sortBy !== "lease_remaining" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("lease_remaining");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('noi') && (
                      <th>
                        <div className="table-header">
                          NOI
                          <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('contract_price') && (
                      <th>
                        <div className="table-header">
                          CONTRACT PRICE
                          <img role="button" src={sortBy !== "contract_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('contract_cap_rate') && (
                      <th>
                        <div className="table-header">
                          CONTRACT CAP RATE
                          <img role="button" src={sortBy !== "contract_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_cap_rate"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('asking_price_percent') && (
                      <th>
                        <div className="table-header">
                          % ASKING PRICE
                          <img role="button" src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("asking_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('gci_to_company') && (
                      <th>
                        <div className="table-header">
                          Gci to company
                          <img role="button" src={sortBy !== "gci_to_company" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("gci_to_company"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('actions') && (
                      <th><div className="table-header">Actions</div></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {listData?.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="dark-H body-N">
                        {visibleColumns.includes('details') && (
                          <td>
                            <img role="button" src={expandedRows[el.id] ? UpArrow : DownArrow} alt="toggle" className="sidebar-icons" onClick={() => toggleRowDetails(el.id)} />
                          </td>
                        )}
                        {visibleColumns.includes('contract_date') && (
                          <td>{moment(el?.created_at).tz('America/New_York').format("MM/DD/YY")}</td>
                        )}
                        {visibleColumns.includes('property_name') && (
                          <td className="head-5 capitalize">
                            <Link
                              to="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (el?.portfolio === 0) navigate(ROUTES.PROPERTY_DETAILS.replace(":id", el?.executed?.property_id));
                                else if (el?.executed?.link?.owner_info_type === "contact") navigate(ROUTES.CONTACT_DETAILS.replace(":id", el?.executed?.link?.owner?.id));
                                else if (el?.executed?.link?.owner_info_type === "company") navigate(ROUTES.COMPANY_DETAILS.replace(":id", el?.executed?.link?.owner?.id));
                              }}
                            >
                              {el?.executed?.link?.property_name}<br />
                              {el?.executed?.link?.street_address}<br />
                              {el?.executed?.link?.city && el?.executed?.link?.state ? `${el.executed.link.city}, ${el.executed.link.state}` : ""}
                            </Link>
                          </td>
                        )}
                        {visibleColumns.includes('acquisition') && (
                          <td>
                            <Link
                              to={ROUTES.CONTACT_DETAILS.replace(":id", el?.executed?.offer?.buyer_contact_id)}
                              className="body-N dark-H"
                              onClick={(e) => {
                                e.preventDefault()
                                navigate(ROUTES.CONTACT_DETAILS.replace(":id", el?.executed?.offer?.buyer_contact_id))
                              }}
                            >
                              {el?.executed?.offer?.acquistion_name || ""}
                            </Link>
                          </td>
                        )}
                        {visibleColumns.includes('disposition') && (
                          <td>
                            <Link
                              to={ROUTES.CONTACT_DETAILS.replace(":id", el?.executed?.offer?.seller_contact_id)}
                              className="body-N dark-H"
                              onClick={(e) => {
                                e.preventDefault()
                                navigate(ROUTES.CONTACT_DETAILS.replace(":id", el?.executed?.offer?.seller_contact_id))
                              }}
                            >
                              {el?.executed?.offer?.disposition_name || ""}
                            </Link>
                          </td>
                        )}
                        {visibleColumns.includes('lease_type') && (
                          <td className="head-5 capitalize text-center">
                            {el?.executed?.link?.lease_type}
                          </td>
                        )}
                        {visibleColumns.includes('lease_term_remaining') && (
                          <td className="head-5 capitalize text-center">
                            {el?.executed?.lease_term_remaining}
                          </td>
                        )}
                        {visibleColumns.includes('noi') && (
                          <td className="text-center">${el?.executed?.link?.noi_format}</td>
                        )}
                        {visibleColumns.includes('contract_price') && (
                          <td className="text-center">${el?.contract_price_format}</td>
                        )}
                        {visibleColumns.includes('contract_cap_rate') && (
                          <td className="text-center">{el?.contract_cap_rate_format}%</td>
                        )}
                        {visibleColumns.includes('asking_price_percent') && (
                          <td className="text-center">{el?.asking_price_format_percentage}%</td>
                        )}
                        {visibleColumns.includes('gci_to_company') && (
                          <td className="text-center">${el?.gross_commission?.gci_to_company}</td>
                        )}
                        {visibleColumns.includes('actions') && (
                          <td className="flex gap-3 items-center justify-center">
                            <ActionsMenu
                              from="under-contract"
                              handleEdit={() => {
                                setShowEditModal(true)
                                setSelectedId(el?.id)
                              }}
                              handleDelete={() => {
                                setSelectedId(el?.id)
                                setShowDeleteModal(true)
                              }}
                              showOtherOption={true}
                              otherOptionTitle={"Convert to Closed"}
                              handleOtherOption={() => {
                                setShowConfirmModal(true)
                                setSelectedId(el?.id)
                              }}
                              felloutcontractoption={"Fell Out Of Contract"}
                              handlefelloutcontractOption={() => {
                                setShowFellOutContract(true)
                                setSelectedId(el?.id)
                              }}
                            />
                          </td>
                        )}
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-6 items-start">
                                  {/* Asking Cap Rate */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Asking Cap Rate</p>
                                    <p className="body-N dark-H">
                                      {el?.link?.asking_cap_rate_format ? `${el?.link?.asking_cap_rate_format}%` : "0"}
                                    </p>
                                  </div>

                                  {/* Asking Price */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Asking Price</p>
                                    <p className="body-N dark-H">
                                      {el?.link?.asking_price_format ? `$${el?.link?.asking_price_format}` : "0"}
                                    </p>
                                  </div>

                                  {/* Seller Broker */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.executed?.offer?.seller_broker?.length > 0 ? (
                                        el.executed.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.executed.offer.seller_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Seller BDS */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.executed?.offer?.seller_bds?.first_name} {el?.executed?.offer?.seller_bds?.last_name || ""}
                                    </p>
                                  </div>

                                  {/* Buyer Broker */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.executed?.offer?.buyer_broker?.length > 0 ? (
                                        el.executed.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.executed.offer.buyer_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Buyer BDS */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.executed?.offer?.buyer_bds?.first_name} {el?.executed?.offer?.buyer_bds?.last_name || ""}
                                    </p>
                                  </div>
                                </div>


                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.executed?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.executed?.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.executed?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.executed?.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
          )}
        </div>

        {listData?.length > 0 && (
          <ContactPagination
            activeTab={activeView}
            paginationData={paginationData}
            handleNext={handleNext}
            handlePrev={handlePrev}
            itemPerPage={itemPerPage}
          />
        )}
      </div>

      {/* Modals */}
      <AddUnderContractModal showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />
      <EditUnderContractData showModal={showEditModal} onClose={() => setShowEditModal(false)} id={selectedId} onCallApi={() => { setSelectedId(""); onSuccess(); }} />
      <ConvertClosedModal showModal={showConfirmModal} id={selectedId} onClose={() => setShowConfirmModal(false)} handleConfirm={handleConfirm} />
      <FellOutContract from="undercontact" showModal={showfelloutcontract} id={selectedId} onClose={() => setShowFellOutContract(false)} onCallApi={onSuccess} />
      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />

      {showColumnModal && (
        <ColumnCustomizer
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          storageKey="underContractVisibleColumns"
          onClose={() => setShowColumnModal(false)}
        />
      )}
    </BaseLayout>
  );
};

export default UnderContract;